<template>
  <div class="min-h-screen bg-dark-900">
    <!-- Navigation -->
    <nav class="bg-dark-800 border-b border-white/10 px-6 py-4">
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <div class="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <QrCodeIcon class="w-6 h-6 text-white" />
          </div>
          <span class="text-xl font-bold text-gradient">Digital Menu AI</span>
        </div>
        <div class="flex items-center space-x-4">
          <span class="text-gray-300">Welcome, {{ user?.firstName }}</span>
          <button @click="logout" class="text-gray-400 hover:text-white transition-colors">
            <LogOutIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </nav>

    <div class="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <!-- Header -->
      <header class="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <router-link 
                to="/dashboard"
                class="text-white hover:text-blue-200 transition-colors"
              >
                ← Back to Dashboard
              </router-link>
              <h1 class="text-2xl font-bold text-white">Category Management</h1>
            </div>
            <button
              @click="showCreateModal = true"
              class="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center gap-2"
            >
              + Add Category
            </button>
          </div>
        </div>
      </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="text-white text-lg">Loading categories...</div>
      </div>

      <!-- Categories Grid -->
      <div v-else-if="categories.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="category in sortedCategories"
          :key="category.id"
          class="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all"
        >
          <!-- Category Image -->
          <div class="w-full h-32 bg-white/5 rounded-lg mb-4 flex items-center justify-center">
            <img
              v-if="category.imageUrl"
              :src="category.imageUrl"
              :alt="category.name"
              class="w-full h-full object-cover rounded-lg"
            />
            <div v-else class="text-white/50 text-4xl">
              📁
            </div>
          </div>

          <!-- Category Info -->
          <div class="mb-4">
            <div class="flex justify-between items-start mb-2">
              <h3 class="text-xl font-semibold text-white">{{ category.name }}</h3>
              <span class="text-sm text-white/60 bg-white/10 px-2 py-1 rounded">Order: {{ category.order || 1 }}</span>
            </div>
            <p class="text-white/70 text-sm">{{ category.description || 'No description' }}</p>
          </div>

          <!-- Category Stats -->
          <div class="text-white/50 text-sm mb-4">
            Created: {{ formatDate(category.createdAt) }}
          </div>

          <!-- Actions -->
          <div class="flex space-x-2">
            <button
              @click="editCategory(category)"
              class="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors shadow-md hover:shadow-lg"
            >
              Edit
            </button>
            <button
              @click="deleteCategory(category.id)"
              class="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors shadow-md hover:shadow-lg"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <div class="text-white/50 text-6xl mb-4">📂</div>
        <h3 class="text-xl font-semibold text-white mb-2">No Categories Yet</h3>
        <p class="text-white/70 mb-6">Create your first category to organize your menu items.</p>
        <button
          @click="showCreateModal = true"
          class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          Create First Category
        </button>
      </div>
    </main>

    <!-- Create/Edit Category Modal -->
    <div
      v-if="showCreateModal || editingCategory"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div class="bg-white/10 backdrop-blur-md rounded-xl p-6 w-full max-w-md border border-white/20">
        <h2 class="text-xl font-semibold text-white mb-6">
          {{ editingCategory ? 'Edit Category' : 'Create New Category' }}
        </h2>

        <form @submit.prevent="saveCategory" class="space-y-4">
          <!-- Name and Order -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Name</label>
              <input
                v-model="categoryForm.name"
                type="text"
                required
                class="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Category name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Display Order</label>
              <input
                v-model.number="categoryForm.order"
                type="number"
                min="1"
                class="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Order (1, 2, 3...)"
              />
            </div>
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              v-model="categoryForm.description"
              rows="3"
              class="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Category description (optional)"
            ></textarea>
          </div>

          <!-- Image URL -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
            <input
              v-model="categoryForm.imageUrl"
              type="url"
              class="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <!-- Buttons -->
          <div class="flex space-x-3 pt-4">
            <button
              type="button"
              @click="closeModal"
              class="flex-1 bg-white/10 hover:bg-white/20 text-white py-2 px-4 rounded-lg font-medium transition-colors border border-white/20"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!categoryForm.name.trim()"
              class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors"
            >
              {{ editingCategory ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { useToast } from '@/composables/useToast'
import { QrCode as QrCodeIcon, LogOut as LogOutIcon } from 'lucide-vue-next'

export default {
  name: 'CategoryManagement',
  components: {
    QrCodeIcon,
    LogOutIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      categories: [],
      loading: true,
      showCreateModal: false,
      editingCategory: null,
      user: null, // Add user data
      categoryForm: {
        name: '',
        description: '',
        imageUrl: '',
        order: 1
      }
    }
  },
  computed: {
    sortedCategories() {
      return [...this.categories].sort((a, b) => {
        // Sort by order first, then by name
        if (a.order !== b.order) {
          return (a.order || 1) - (b.order || 1)
        }
        return a.name.localeCompare(b.name)
      })
    }
  },
  async mounted() {
    // Load user data from localStorage
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        this.user = JSON.parse(userStr)
      } catch (error) {
        console.error('Failed to parse user data:', error)
        this.user = null
      }
    }

    await this.loadCategories()
  },
  methods: {
    async loadCategories() {
      try {
        this.loading = true
        this.categories = await api.getCategories()
      } catch (error) {
        console.error('Error loading categories:', error)
        this.toast.error('Loading Failed', 'Failed to load categories: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    editCategory(category) {
      this.editingCategory = category
      this.categoryForm = {
        name: category.name,
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        order: category.order || 1
      }
    },

    closeModal() {
      this.showCreateModal = false
      this.editingCategory = null
      this.categoryForm = {
        name: '',
        description: '',
        imageUrl: '',
        order: 1
      }
    },

    async saveCategory() {
      try {
        if (this.editingCategory) {
          // Update existing category
          const updatedCategory = await api.updateCategory(this.editingCategory.id, this.categoryForm)
          const index = this.categories.findIndex(c => c.id === this.editingCategory.id)
          this.categories[index] = updatedCategory
          this.toast.success('Category Updated', 'Category updated successfully')
        } else {
          // Create new category
          const newCategory = await api.createCategory(this.categoryForm)
          this.categories.push(newCategory)
          this.toast.success('Category Created', 'Category created successfully')
        }
        this.closeModal()
      } catch (error) {
        console.error('Error saving category:', error)
        this.toast.error('Save Failed', 'Failed to save category: ' + error.message)
      }
    },

    async deleteCategory(categoryId) {
      if (!confirm('Are you sure you want to delete this category? This will also delete all menu items in this category.')) {
        return
      }

      try {
        await api.deleteCategory(categoryId)
        this.categories = this.categories.filter(c => c.id !== categoryId)
        this.toast.success('Category Deleted', 'Category deleted successfully')
      } catch (error) {
        console.error('Error deleting category:', error)
        this.toast.error('Delete Failed', 'Failed to delete category: ' + error.message)
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },

    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    }
  }
}
</script>
